/**
 * Cross-host URL builders. Use these whenever a link crosses surfaces:
 *
 *   apex `loci.city/about`              → apexUrl('/about')
 *   city `clt.loci.city/event/<slug>`   → cityUrl('CLT', '/event/foo-fest')
 *   app  `app.loci.city/clt/saved`      → appUrl({ city: 'CLT', path: '/saved' })
 *
 * Within a surface, prefer plain relative paths in `<Link>`; reach for these
 * helpers only when the destination is on a different host.
 *
 * Reads `NEXT_PUBLIC_LOCI_ROOT_DOMAIN` (default `loci.city`) and resolves
 * staging via {@link resolveIsStaging}: request host / `x-is-staging` first,
 * then `NEXT_PUBLIC_LOCI_STAGE` (inlined at build time), then
 * `window.location` on the client. A forgotten staging env must not emit
 * production hosts from `staging-clt.loci.city` (WEB-776).
 *
 * Stays browser-safe and does not import `@/config/env`, which transitively
 * pulls in `@loci/config`'s `loadEnv.ts` (Node-only `fs`/`path`/`dotenv`) and
 * breaks Storybook's Vite bundle when this module is imported by a
 * story-bundled client component (APO-207). Server-side env validation still
 * happens once at startup via `next.config.js`.
 */

import { CITY_EDITIONS, type CityCode } from '@loci/city-editions';

import { DEFAULT_CITY_CODE_FALLBACK } from '@/config/city-routing-defaults';
import { resolveIsStaging, type CrossHostStageOptions } from '@/lib/loci-stage';

export type { CrossHostStageOptions };

const KNOWN_CITY_CODES: ReadonlySet<CityCode> = new Set(Object.keys(CITY_EDITIONS) as CityCode[]);

function rootDomain(): string {
  return process.env.NEXT_PUBLIC_LOCI_ROOT_DOMAIN ?? 'loci.city';
}

/**
 * Resolve the default `CityCode` for cross-host links emitted from surfaces
 * that have no per-request city context (e.g. the apex B2B portal pages).
 *
 * Reads `NEXT_PUBLIC_DEFAULT_CITY_CODE` (a public env var inlined by Next.js
 * at build time) and falls back to `DEFAULT_CITY_CODE_FALLBACK` (shared with
 * the env schema and the proxy fallback in `proxy.ts` so all three cannot
 * drift). The candidate is upper-cased and then validated against the live
 * `CITY_EDITIONS` registry — only registered city codes are returned, so a
 * stale or typo'd env value never produces a broken
 * `https://app.loci.city/<unregistered>/studio` URL.
 *
 * Co-located with `cityUrl()` so portal pages need a single import:
 *
 * ```ts
 * import { cityUrl, defaultCityCode } from '@/lib/cross-host-urls';
 * const href = cityUrl(defaultCityCode(), '/studio');
 * ```
 */
export function defaultCityCode(): CityCode {
  const raw = process.env.NEXT_PUBLIC_DEFAULT_CITY_CODE;
  const candidate = raw?.toUpperCase() as CityCode | undefined;
  return candidate && KNOWN_CITY_CODES.has(candidate) ? candidate : DEFAULT_CITY_CODE_FALLBACK;
}

function isStaging(stage?: CrossHostStageOptions): boolean {
  return resolveIsStaging(stage);
}

function scheme(domain: string): 'http' | 'https' {
  return domain.startsWith('loci.localhost') ? 'http' : 'https';
}

function buildHost(prefix: string | null): string {
  const domain = rootDomain();
  const proto = scheme(domain);
  if (!prefix) return `${proto}://${domain}`;
  return `${proto}://${prefix}.${domain}`;
}

/**
 * Normalize a caller-provided path so it always begins with `/`. Without this,
 * `apexUrl('about')` would produce `https://loci.cityabout` — concatenation
 * doesn't care that `about` lacks a leading slash. Centralizing the fix here
 * keeps `apexUrl`, `cityUrl`, and `appUrl` consistent without forcing every
 * caller to remember the convention.
 */
function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

export function apexUrl(path: string, stage?: CrossHostStageOptions): string {
  const prefix = isStaging(stage) ? 'staging' : null;
  return `${buildHost(prefix)}${normalizePath(path)}`;
}

export function cityUrl(city: CityCode, path: string, stage?: CrossHostStageOptions): string {
  const cityLower = city.toLowerCase();
  const prefix = isStaging(stage) ? `staging-${cityLower}` : cityLower;
  return `${buildHost(prefix)}${normalizePath(path)}`;
}

/**
 * Absolute URL of a page on the given city's host, for threading through
 * `crossHostLogin` / `crossHostSignup` as `next` (WEB-898) so a cross-host
 * sign-in returns the visitor to the city page they were reading instead of
 * the app feed. `search` is the query string WITHOUT a leading `?` (i.e.
 * `URLSearchParams#toString()`); pass `''` when there is none.
 */
export function cityReturnUrl(
  city: CityCode,
  pathname: string,
  search: string,
  stage?: CrossHostStageOptions,
): string {
  const path = search ? `${pathname}?${search}` : pathname;
  return cityUrl(city, path, stage);
}

export interface AppUrlOptions extends CrossHostStageOptions {
  /** City-scoped app URL when set; city-agnostic (auth surface) when null. */
  city: CityCode | null;
  /** Path relative to the app surface — e.g. `/saved`, `/login`, `/studio/events/new`. */
  path: string;
  /** Optional `?next=` redirect target appended to auth flows. */
  next?: string;
}

export function appUrl(options: AppUrlOptions): string {
  const prefix = isStaging(options) ? 'staging.app' : 'app';
  const base = buildHost(prefix);
  const cityPrefix = options.city ? `/${options.city.toLowerCase()}` : '';
  const url = `${base}${cityPrefix}${normalizePath(options.path)}`;
  if (options.next === undefined) return url;
  const query = new URLSearchParams({ next: options.next });
  return `${url}?${query.toString()}`;
}

/**
 * True when `href` is an absolute URL on the authed app host
 * (`app.<root>` or `staging.app.<root>`). Chrome that opens other-host links
 * in a new tab uses this to keep a signed-in Discovery hop in the same tab:
 * moving a person onto `app.*` is navigation, not an outbound link
 * (WEB-1231 / R25).
 */
export function isAppHostHref(href: string): boolean {
  if (!/^https?:\/\//i.test(href)) return false;
  try {
    const host = new URL(href).host;
    const domain = rootDomain();
    return host === `app.${domain}` || host === `staging.app.${domain}`;
  } catch {
    return false;
  }
}

/**
 * Canonical authed-app entry point for a city — e.g.
 * `https://app.loci.city/clt`. This is the single source of truth for every
 * "Open app" / primary-entry CTA emitted from the marketing-city surface
 * (home hero, header, footer download strip) so they cannot drift apart or
 * regress to a `/coming-soon` dead-end (WEB-329).
 *
 * Defaults the city to `defaultCityCode()` (Charlotte for the MVP, validated
 * against the live `CITY_EDITIONS` registry). Unlike `appUrl({ city, path:
 * '/' })`, the city root is emitted *without* a trailing slash so the href
 * matches the canonical `app.loci.city/<city>` form exactly. Staging-aware
 * via the shared `buildHost`.
 */
export function cityAppEntryUrl(
  city: CityCode = defaultCityCode(),
  stage?: CrossHostStageOptions,
): string {
  const prefix = isStaging(stage) ? 'staging.app' : 'app';
  return `${buildHost(prefix)}/${city.toLowerCase()}`;
}

/**
 * Display host label for the **marketing-city** footer locator
 * (`CITY · {hostLabel} · {coords}`), e.g. `clt.loci.city` in production or
 * `staging-clt.loci.city` on staging. Derived from `cityUrl` so the label
 * tracks `NEXT_PUBLIC_LOCI_STAGE` / `NEXT_PUBLIC_LOCI_ROOT_DOMAIN` instead of
 * being hardcoded — staging and localhost render the host they actually serve
 * from. Returns the bare host (scheme + path stripped).
 */
export function cityHostLabel(city: CityCode, stage?: CrossHostStageOptions): string {
  return new URL(cityUrl(city, '/', stage)).host;
}

/**
 * Display host label for the **signed-in app** footer locator
 * (`APP · {hostLabel}`), e.g. `app.loci.city/clt` in production or
 * `staging.app.loci.city/clt` on staging. Derived from `cityAppEntryUrl` so it
 * stays staging-aware and cannot drift from the canonical app entry point.
 * Returns `{host}{/city}` (scheme stripped, city path retained).
 */
export function appHostLabel(
  city: CityCode = defaultCityCode(),
  stage?: CrossHostStageOptions,
): string {
  const url = new URL(cityAppEntryUrl(city, stage));
  return `${url.host}${url.pathname}`;
}
